<template>
  <ReferenceComponentParentList
    :searchList="result"
    :componentType="componentType"
    :selectedIndex="selectedIndex"
    :command="command"
    :isLast="isLast"
    class="reference-user"
    @onClick="onClick"
    @onScroll="onScroll"
  >
    <template #header>
      <div class="reference-user__header">
        <BaseSubHeading
          weight="600"
          size="large"
          color="monochrome-06"
          className="reference-user__title"
        >
          Team
        </BaseSubHeading>
        <BasicCheckbox
          v-if="showTaskOwnersFilter"
          label="Task owners only"
          value="taskOwnersOnly"
          :modelValue="isTaskOwnersOnly"
          checkType="switch"
          @update:modelValue="handleCheckbox"
        />
      </div>
    </template>
    <template #item="{ option }">
      <div class="reference-user__item">
        <div class="reference-user__left">
          <Avatar
            :src="option.pictureUrl"
            :gap="option.getInitials()"
          />
        </div>
        <div class="reference-user__right">
          <BaseSubHeading
            weight="400"
            size="large"
            color="monochrome-06"
            className="reference-user__title"
          >
            {{ option.fullName }}
          </BaseSubHeading>
          <BaseSubHeading
            v-if="hasDuplicateName(option.fullName)"
            weight="500"
            size="medium"
            color="monochrome-05"
            darkColor="monochrome-04"
            className="reference-user__desc"
          >
            ({{ option.email }})
          </BaseSubHeading>
        </div>
      </div>
    </template>
  </ReferenceComponentParentList>
</template>

<script lang="ts">
import { mixins, Options } from "vue-class-component"
import { Action, Getter } from "s-vuex-class"

import Avatar from "@/components/Avatar/index.vue"
import BasicCheckbox from "@/components/Form/BasicCheckbox.vue"
import ReferenceComponentListCommon from "@/components/Reference/helpers/ReferenceComponentListCommon"
import ReferenceComponentParentList from "@/components/Reference/helpers/ReferenceComponentParentList.vue"

import { Item, ServerResponse } from "@/models"
import { FilterUser } from "@/models/Filter/FilterUser"
import { User } from "@/models/User"
import { ReferenceType } from "@/plugins/extensions/ReferenceType"
import { Actions, Getters } from "@/store"
import { parsingServerResponseContent } from "@/utils/helpers"

const components = {
  Avatar,
  ReferenceComponentParentList,
  BasicCheckbox
}

@Options({
  name: "UserList",
  components
})
export default class extends mixins(ReferenceComponentListCommon) {
  @Getter(Getters.GET_SESSION_TEAM_ID) readonly sessionTeamId: string
  @Getter(Getters.GET_USER_CURRENT_PROFILE) readonly curentUser: User
  @Getter(Getters.GET_ITEM) item: Item
  @Getter(Getters.USER_SEARCH_TEXT) searchText: string

  @Action(Actions.GET_USERS) findUsers: (params: FilterUser) => Promise<ServerResponse<User>>
  @Action(Actions.USER_SEARCH_TEXT) setSearchText: (text: string) => void

  filter: FilterUser = FilterUser.createDefault()
  result: User[] = []
  isLast = true

  async created() {
    this.componentType = ReferenceType[this.items.type]
    this.filter.includeEnabledOnly = true
    this.applyUserScopeFilter()
    if (this.searchText) {
      this.filter.text = this.searchText
      this.setSearchText("")
    }
    this.filter.forSearch = true
    this.searchAction = this.onSearchUsers
    this.search()
  }

  get isAllUsers() {
    return this.items.type === "PREVIOUS_TASK_OWNER"
  }

  get canSwitchToAllUsers() {
    const extensions = this.editor.extensionManager.extensions
    return extensions.find(ext => ext.name === "referenceAllUser")
  }

  get isTaskOwnersOnly() {
    return Boolean(this.filter.currentTaskOwnersByItemId)
  }

  get showTaskOwnersFilter() {
    return this.canSwitchToAllUsers && this.isAllUsers && Boolean(this.item.id)
  }

  get teamId() {
    return this.item.team?.id ?? this.sessionTeamId
  }

  get currentProjectId() {
    return this.item.projectId ?? this.item.project?.id ?? this.project?.id ?? this.projectId
  }

  handleCheckbox(value: boolean) {
    this.filter.page = 1
    this.filter.currentTaskOwnersByItemId = value && this.item.id ? this.item.id : ""
    this.search()
    this.editor.view.focus()
  }

  applyUserScopeFilter(taskOwnersOnly = false) {
    this.filter.excludeTeamIds = []
    if (this.isAllUsers) {
      this.filter.projectId = this.currentProjectId
      this.filter.teamId = ""
      this.filter.currentTaskOwnersByItemId = taskOwnersOnly && this.item.id ? this.item.id : ""
      return
    }

    this.filter.projectId = ""
    this.filter.teamId = this.teamId
    this.filter.currentTaskOwnersByItemId = ""
  }

  hasDuplicateName(fullName: string): boolean {
    const duplicate = this.result.filter(user => user.fullName === fullName)

    return duplicate.length > 1
  }

  queryUpdated(q) {
    const taskOwnersOnly = this.isTaskOwnersOnly
    this.filter.page = 1
    this.filter.text = q ?? ""
    this.applyUserScopeFilter(taskOwnersOnly)
    this.search()
  }

  onEnterHandler() {
    this.onClick(this.result[this.selectedIndex])
  }

  onClick(user: User) {
    if (!user) return
    this.selectItem({
      item: user,
      payload: {
        showDetails: this.hasDuplicateName(user.fullName)
      }
    })
  }

  async onSearchUsers() {
    this.filter.includeDemo = this.project?.demo ?? false
    const serverResponseUser = await this.findUsers(this.filter)
    if (!serverResponseUser) return
    const content = parsingServerResponseContent({
      originalContent: this.result,
      serverResponseContent: serverResponseUser.content,
      numberOfPage: serverResponseUser.pageable.pageNumber
    })
    this.result = content.filter(({ id }) => id !== this.curentUser?.id)
    this.isLast = serverResponseUser.last
  }
}
</script>
